import React, { useState } from "react";
import { Box, Button, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import {
  tokens,
  StatusPill,
  CodeChip,
  PatientStrip,
  ClosedLoopTracker,
  SectionPanel,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import {
  FOLLOW_UP_STATUS,
  FOLLOW_UP_META,
  TCM_WINDOW,
  TCM_WINDOW_META,
  CONTACT_CHANNELS,
  CONTACT_OUTCOMES,
} from "../common/constants";

/** Mapea el follow-up status a los pasos del closed-loop (§5.6). */
function buildLoopSteps(status) {
  const S = FOLLOW_UP_STATUS;
  switch (status) {
    case S.PENDING:
      return ["done", "active", "pending", "pending"];
    case S.OUTREACH:
      return ["done", "active", "pending", "pending"];
    case S.SCHEDULED:
      return ["done", "done", "done", "active"];
    case S.NO_SHOW:
      return ["done", "done", "done", "blocked"];
    case S.COMPLETED:
    default:
      return ["done", "done", "done", "done"];
  }
}

const LABEL_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: tokens.ink3,
};
const VALUE_SX = {
  fontFamily: tokens.fontMono,
  fontVariantNumeric: "tabular-nums",
  fontSize: 14,
  color: tokens.ink,
};

function Field({ label, children }) {
  return (
    <Box>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography component="div" sx={VALUE_SX}>{children ?? "—"}</Typography>
    </Box>
  );
}

/**
 * Panel de detalle del alta seleccionada: patient strip, closed-loop tracker,
 * indicadores de ventana TCM (7/14 días) y acciones de contacto (estado local
 * — sin backend todavía).
 */
export default function DischargeDetailPanel({ row, locationLabel }) {
  const [contactLog, setContactLog] = useState([]);
  const [channel, setChannel] = useState(CONTACT_CHANNELS[0]);
  const [outcome, setOutcome] = useState(CONTACT_OUTCOMES[0]);

  if (!row) {
    return (
      <SectionPanel title="Discharge detail">
        <ClinicalEmptyState message="Select a discharge from the work queue to review the transition." />
      </SectionPanel>
    );
  }

  const statusMeta = FOLLOW_UP_META[row.followUpStatus] || FOLLOW_UP_META.PENDING;
  const tcmMeta = TCM_WINDOW_META[row.tcmWindow] || TCM_WINDOW_META.EXPIRED;
  const [s1, s2, s3, s4] = buildLoopSteps(row.followUpStatus);

  const logContact = () => {
    setContactLog((prev) => [
      {
        at: new Date().toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" }),
        channel,
        outcome,
      },
      ...prev,
    ]);
  };

  return (
    <Stack spacing={1.5}>
      <PatientStrip
        patient={{
          name: row.name,
          cipId: row.cipId,
          location: locationLabel,
          pcp: row.pcpName,
        }}
        actions={<StatusPill tone={statusMeta.tone} label={statusMeta.label} />}
      />

      <SectionPanel title="Transition closed-loop">
        <ClosedLoopTracker
          steps={[
            { label: "Discharge detected", state: s1 },
            { label: "Outreach", state: s2 },
            { label: "Appointment scheduled", state: s3 },
            { label: "Visit completed", state: s4 },
          ]}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
          <StatusPill tone={tcmMeta.tone} label={tcmMeta.label} dense />
          {row.tcmWindow !== TCM_WINDOW.EXPIRED && (
            <StatusPill
              tone={row.tcmDaysRemaining <= 3 ? "crit" : "info"}
              label={`${row.tcmDaysRemaining} day(s) left in 14-day window`}
              dense
            />
          )}
          <StatusPill
            tone="muted"
            label={`Target visit by ${row.targetVisitDate || "—"}`}
            dense
          />
        </Stack>
      </SectionPanel>

      <SectionPanel title="Index event">
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, md: 4 }}>
            <Field label="Facility">{row.facility || "Unknown facility"}</Field>
          </Grid>
          <Grid size={{ xs: 3, md: 2 }}>
            <Field label="LACE+">
              <CodeChip
                code={String(row.laceScore ?? "—")}
                tone={(row.laceScore ?? 0) >= 13 ? "crit" : (row.laceScore ?? 0) >= 10 ? "warn" : "ok"}
              />
            </Field>
          </Grid>
          <Grid size={{ xs: 3, md: 2 }}>
            <Field label="LOS (days)">{row.los}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Admit">{row.admitDate}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Discharge">{row.dischargeDate}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Field label="Attending">{row.attendingPhysician}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 4 }}>
            <Field label="Specialty">{row.attendingSpecialty || "—"}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Disposition">{row.disposition}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Behavioral health">
              {row.behavioralHealth ? (
                <StatusPill tone="warn" label="BH flag" dense />
              ) : (
                "No"
              )}
            </Field>
          </Grid>
        </Grid>
      </SectionPanel>

      <SectionPanel
        title="Contact actions"
        actions={
          <Typography sx={{ fontSize: 11.5, color: tokens.ink3 }}>
            {row.outreachAttempts + contactLog.length} attempt(s) on record
          </Typography>
        }
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }} alignItems="center">
          <Select
            size="small"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            sx={{ fontSize: 13, minWidth: 110, bgcolor: tokens.surface }}
          >
            {CONTACT_CHANNELS.map((c) => (
              <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>{c}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            sx={{ fontSize: 13, minWidth: 230, bgcolor: tokens.surface }}
          >
            {CONTACT_OUTCOMES.map((o) => (
              <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>{o}</MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={logContact}
            sx={{
              bgcolor: tokens.primary,
              "&:hover": { bgcolor: tokens.primary700 },
              textTransform: "none",
              fontSize: 13,
            }}
          >
            Log contact attempt
          </Button>
        </Stack>

        {contactLog.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1.5 }}>
            {contactLog.map((entry, i) => (
              <Stack
                key={`${entry.at}-${i}`}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  px: 1.25,
                  py: 0.5,
                  bgcolor: tokens.surface2,
                  border: `1px solid ${tokens.line}`,
                  borderRadius: "4px",
                }}
              >
                <Typography sx={{ ...VALUE_SX, fontSize: 12, color: tokens.ink3 }}>
                  {entry.at}
                </Typography>
                <CodeChip code={entry.channel} />
                <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>{entry.outcome}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mt: 1 }}>
          Contact log is local-only in this preview — it will persist to
          census.TAB_TRANSITION_OUTREACH_LOG once the backend endpoint is available.
        </Typography>
      </SectionPanel>
    </Stack>
  );
}
